/**
 * @fileoverview Archive-tenant mutation hook.
 * @description Wraps POST /sales/tenants/:id/archive in a TanStack mutation, invalidating every
 * tenant query (list and detail forms alike) on success.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { TENANTS_QUERY_KEY } from '@/features/sales/hooks/useTenants'
import { archiveTenant } from '@/features/sales/services/tenants.service'

/**
 * @description Archives a tenant with a mandatory reason note and refreshes tenant queries.
 * @returns {ReturnType<typeof useMutation<Awaited<ReturnType<typeof archiveTenant>>, unknown, {
 * id: string; reason: string }>>} The mutation object (mutate with `{ id, reason }`, isPending,
 * ...).
 */
export function useArchiveTenant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => archiveTenant(id, reason),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: TENANTS_QUERY_KEY })
    },
  })
}
